import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, FileText, Car, Truck, GaugeCircle, Map } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaClock, FaChartLine, FaPercentage, FaWallet, FaBalanceScale, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { BiCar } from "react-icons/bi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const RenaultCarLoan = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "What’s the minimum interest rate on a Renault car loan?",
      answer: "Starting rates are around 8.5% annually, depending on credit profile.",
    },
    {
      question: "Can I get 100% financing for Renault cars?",
      answer: "Yes, many lenders offer up to 100% of the ex-showroom or on-road price.",
    },
    {
      question: "Are Renault used cars eligible for loans?",
      answer: "Yes, certified used Renault cars can be financed with flexible terms.",
    },
    {
      question: "What credit score is required for approval?",
      answer: "A CIBIL score of 700 or above is preferred by most lenders.",
    },
    {
      question: "Is online loan application available?",
      answer: "Yes, you can compare offers and apply digitally via TheFinMaster.",
    },
    {
      question: "Do I need a co-applicant?",
      answer: "Not mandatory, but a co-applicant can improve loan eligibility.",
    },
    {
      question: "What is the maximum loan tenure offered?",
      answer: "Loan terms go up to 7 years, subject to lender approval.",
    },
    {
      question: "Are car loans available for NRIs buying Renault cars?",
      answer: "Yes, if there's a resident Indian co-applicant.",
    },
    {
      question: "Do women borrowers get any benefits?",
      answer: "Many banks offer discounted rates or relaxed terms for women.",
    },
    {
      question: "What’s the typical processing fee?",
      answer: "It ranges from 0.25% to 1.5% of the total loan amount.",
    },
    {
      question: "Can I pre-close or part-pay the loan?",
      answer: "Yes, but check prepayment charges and policies beforehand.",
    },
    {
      question: "Is hypothecation involved?",
      answer: "Yes, the vehicle remains hypothecated until full repayment.",
    },
    {
      question: "Can I refinance an existing Renault loan?",
      answer: "Yes, via balance transfer to another lender for better terms.",
    },
    {
      question: "Are EMI holiday schemes available?",
      answer: "Some lenders offer deferred EMI options for the initial few months.",
    },
    {
      question: "What is the approval timeline?",
      answer: "Generally between 24 to 72 hours once documents are submitted.",
    },
    {
      question: "What’s the minimum income needed to qualify?",
      answer: "Salaried applicants need a monthly income of at least ₹25,000.",
    },
    {
      question: "Can I finance accessories with the car loan?",
      answer: "Some banks allow funding for car accessories as part of the loan.",
    },
    {
      question: "Is there a loan option for fleet or commercial buyers?",
      answer: "Yes, but terms differ slightly for commercial registrations.",
    },
    {
      question: "Do government employees get better deals?",
      answer: "Yes, certain banks offer lower rates for PSU and government workers.",
    },
    {
      question: "Will the loan cover insurance and registration costs?",
      answer: "Some lenders offer 100% on-road funding, which includes these expenses.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows EMI Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Loans' },
  ];

  const benefits = [
    {
      title: "Renault Kwid",
      desc: "A compact hatchback with SUV-inspired styling and top-tier fuel efficiency.",
      icon: <Car className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Renault Triber",
      desc: "A modular MPV offering 7-seat flexibility at a highly competitive price.",
      icon: <Truck className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Renault Kiger",
      desc: "A stylish and sporty compact SUV loaded with tech and safety features.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Renault Duster (Pre-owned)",
      desc: "A rugged SUV with a legacy of reliability and performance.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
  ];

  const highlights = [
    { icon: <FaRupeeSign className="text-3xl text-blue-700 mb-4" />, feature: "Loan Amount", details: "₹1 Lakh to ₹75 Lakh+" },
    { icon: <FaClock className="text-3xl text-blue-700 mb-4" />, feature: "Loan Tenure", details: "1 to 7 years" },
    { icon: <FaChartLine className="text-3xl text-blue-700 mb-4" />, feature: "Interest Rate Range", details: "8.5% to 14.5% p.a." },
    { icon: <FaPercentage className="text-3xl text-blue-700 mb-4" />, feature: "Processing Fees", details: "0.25% to 1.5% of the loan amount" },
    { icon: <FaWallet className="text-3xl text-blue-700 mb-4" />, feature: "Minimum Down Payment", details: "10% to 25% of the on-road price" },
    { icon: <FaBalanceScale className="text-3xl text-blue-700 mb-4" />, feature: "Loan to Value (LTV)", details: "Up to 100% for eligible applicants" },
    { icon: <FaCheckCircle className="text-3xl text-blue-700 mb-4" />, feature: "Approval Timeline", details: "24–72 hours post-documentation" },
    { icon: <FaExchangeAlt className="text-3xl text-blue-700 mb-4" />, feature: "EMI Options", details: "Regular, Step-Up, Balloon, EMI holiday" },
  ];

  return (
    <>
      <Helmet>
        <title>Renault Car Loan in India – Compare Rates & Apply Online</title>
        <meta
          name="description"
          content="Explore Renault car loan options in India. Compare interest rates, EMI, and apply online for models like Kwid, Triber, Kiger, and more."
        />
        <meta
          name="keywords"
          content="renault car loan, renault kwid loan, renault triber loan, car loan emi calculator"
        />
          <link rel="canonical" href="https://thefinmaster.com/renault-car-loan" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/renault-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Renault Car Loan in India" />
        <meta property="og:description" content="Compare and apply for Renault car loans online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/renault-car-loan" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Renault Car Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Renault Car Loan </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Renault has built a strong presence in India with its practical designs, competitive pricing, and reliable performance. Whether you are planning to buy a budget-friendly Kwid or a versatile Triber, the right car loan can make your purchase smoother and more affordable. This guide walks you through everything you need to know about getting a car loan for a Renault, from interest rates and eligibility to documentation, repayment tips, and FAQs.                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/renaultcarloan.jpeg"
                alt="Renault Car"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            Popular Renault Cars Eligible for Car Loans
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Renault cars are widely financed by leading banks and NBFCs in India. Both new and pre-owned certified Renault vehicles are easily financed with car loans.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="w-full bg-gradient-to-b from-white via-blue-50 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-slate-800 mb-4">
             Renault Car Loan Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-4">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden p-6 rounded-2xl shadow-xl bg-white/70 backdrop-blur-xl border border-gray-200 transition-transform transform hover:scale-105"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-300 via-pink-300 to-yellow-300 rounded-full opacity-30 blur-3xl group-hover:opacity-50 transition-all"></div>
                <div className="text-3xl text-blue-700 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.feature}</h3>
                <p className="text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Banks Offering Renault Car Loans
        </h2>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { lender: "HDFC Bank", rate: "8.50%", tenure: "7 years", features: "Quick approvals with digital KYC" },
              { lender: "SBI", rate: "8.70%", tenure: "7 years", features: "Competitive rates and flexible repayment" },
              { lender: "ICICI Bank", rate: "9.00%", tenure: "7 years", features: "Pre-approved offers and 100% funding" },
              { lender: "Axis Bank", rate: "9.00%", tenure: "5 years", features: "EMI customization and part-payment allowed" },
              { lender: "Kotak Mahindra", rate: "9.25%", tenure: "6 years", features: "Attractive offers for salaried applicants" },
              { lender: "Bank of Baroda", rate: "9.00%", tenure: "7 years", features: "Special schemes for government employees" },
              { lender: "IDFC First Bank", rate: "9.75%", tenure: "6 years", features: "Instant disbursal with low documentation" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.lender}</h3>
                <div className="text-gray-600 mb-1"><strong>Starting Interest Rate:</strong> {item.rate}</div>
                <div className="text-gray-600 mb-1"><strong>Max Tenure:</strong> {item.tenure}</div>
                <div className="text-gray-600"><strong>Key Features:</strong> {item.features}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Lender</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Interest Rate</th>
                  <th className="p-3 border border-gray-300 text-left">Max Tenure</th>
                  <th className="p-3 border border-gray-300 text-left">Key Features</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["HDFC Bank", "8.50%", "7 years", "Quick approvals with digital KYC"],
                  ["SBI", "8.70%", "7 years", "Competitive rates and flexible repayment"],
                  ["ICICI Bank", "9.00%", "7 years", "Pre-approved offers and 100% funding"],
                  ["Axis Bank", "9.00%", "5 years", "EMI customization and part-payment allowed"],
                  ["Kotak Mahindra", "9.25%", "6 years", "Attractive offers for salaried applicants"],
                  ["Bank of Baroda", "9.00%", "7 years", "Special schemes for government employees"],
                  ["IDFC First Bank", "9.75%", "6 years", "Instant disbursal with low documentation"],
                ].map(([lender, rate, tenure, features], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{lender}</td>
                    <td className="p-3 border border-gray-300">{rate}</td>
                    <td className="p-3 border border-gray-300">{tenure}</td>
                    <td className="p-3 border border-gray-300">{features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Loan EMI <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your EMI — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-loan-emi-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Sample EMI Calculation for Renault Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Use TheFinMaster’s EMI Calculator to adjust loan tenure, interest rate, and down payment to plan your finances more effectively.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Renault Kwid", price: "₹6.5 Lakh", loan: "₹5.8 Lakh", emi: "₹11,800" },
              { model: "Renault Triber", price: "₹8.5 Lakh", loan: "₹7.6 Lakh", emi: "₹15,300" },
              { model: "Renault Kiger", price: "₹10 Lakh", loan: "₹9 Lakh", emi: "₹18,500" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>On-Road Price:</strong> {item.price}</div>
                <div className="text-gray-600 mb-1"><strong>Loan Amount:</strong> {item.loan}</div>
                <div className="text-gray-600"><strong>EMI (5 Years @10%):</strong> {item.emi}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">On-Road Price (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Loan Amount (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">EMI (5 Years @10%)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Renault Kwid", "₹6.5 Lakh", "₹5.8 Lakh", "₹11,800"],
                  ["Renault Triber", "₹8.5 Lakh", "₹7.6 Lakh", "₹15,300"],
                  ["Renault Kiger", "₹10 Lakh", "₹9 Lakh", "₹18,500"],
                ].map(([model, price, loan, emi], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{price}</td>
                    <td className="p-3 border border-gray-300">{loan}</td>
                    <td className="p-3 border border-gray-300">{emi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Renault Car Loan
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Salaried Individuals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300 mb-6">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Latest 3–6 months’ salary slips</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Form 16 or last 2 years’ ITR</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 6 months’ bank statement</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Address proof (utility bill, rent agreement, etc.)</span>
              </div>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Self-Employed Individuals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business registration or GST certificate</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>ITRs for the last 2–3 years</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Audited financial statements</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business address proof and bank statements</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BiCar />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Renault offers excellent value across hatchbacks, MPVs, and SUVs, making it a strong choice for budget-conscious Indian buyers. With a wide range of car loan options from top banks, purchasing a Renault vehicle becomes easier and more affordable.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Use TheFinMaster to compare car loan offers, calculate EMIs, and apply online — all in one place. Let your Renault journey begin with the right financial partner.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default RenaultCarLoan;